import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { leaveApi } from "../leaveApi";
import { leavePolicyApi } from "../leavePolicyApi";
import { queryKeys } from "./queryKeys";
import useInvalidatingMutation from "./useInvalidatingMutation";

/** Count of leave requests (e.g. pending) for the dashboard → number */
export const useCompanyLeaveRequestCount = (params) =>
  useQuery({
    queryKey: queryKeys.leave.companyRequestCount(params),
    queryFn: () => leaveApi.getCompanyRequestCount(params),
    enabled: !!params?.companyId,
  });

export const useLeavePolicies = ({ enabled = true } = {}) =>
  useQuery({
    queryKey: queryKeys.leave.policies(),
    queryFn: leavePolicyApi.getAll,
    enabled,
  });

export const useCreateLeavePolicy = () =>
  useInvalidatingMutation(
    (data) => leavePolicyApi.createLeavePolicy(data),
    [queryKeys.leave.policies()],
  );

export const useUpdateLeavePolicy = () =>
  useInvalidatingMutation(
    ({ id, data }) => leavePolicyApi.updateLeavePolicy(id, data),
    [queryKeys.leave.policies()],
  );

export const useDeleteLeavePolicy = () =>
  useInvalidatingMutation(
    (id) => leavePolicyApi.deleteLeavePolicy(id),
    [queryKeys.leave.policies()],
  );

/* ---------- Leave allocation (Admin/HR) ---------- */

export const allocationKeys = {
  list: (filters) => ["leaveAllocations", filters],
  history: (employeeId, year) => ["allocationHistory", employeeId, year],
};

/** Employees with their balances for one year → { year, meta, data } */
export const useLeaveAllocations = (filters) =>
  useQuery({
    queryKey: allocationKeys.list(filters),
    queryFn: () => leaveApi.getAllEmployeeBalance(filters),
    placeholderData: keepPreviousData,
  });

export const useAllocationHistory = (
  employeeId,
  year,
  { enabled = true } = {},
) =>
  useQuery({
    queryKey: allocationKeys.history(employeeId, year || "all"),
    queryFn: async () => {
      const res = await leaveApi.getAllocationHistory(employeeId, year);
      return Array.isArray(res) ? res : res?.data || [];
    },
    enabled: !!employeeId && enabled,
  });

/**
 * Put the saved balance into every cached allocation page (no refetch): it
 * replaces the same type's balance for the same cycle, or is added.
 */
const patchAllocationPages = (qc, balance) =>
  qc.setQueriesData({ queryKey: ["leaveAllocations"] }, (page) => {
    if (!page?.data) return page;
    return {
      ...page,
      data: page.data.map((emp) => {
        if (emp.id !== balance.employeeId) return emp;
        const sameCycle = (b) =>
          b.leaveTypeId === balance.leaveTypeId &&
          (b.periodStart
            ? b.periodStart === balance.periodStart
            : b.year === balance.year);
        const others = (emp.leaveBalances || []).filter((b) => !sameCycle(b));
        return {
          ...emp,
          leaveBalances: [
            ...others,
            { hasPolicy: !!balance.policy || !!balance.policyId, ...balance },
          ],
        };
      }),
    };
  });

/**
 * Set (or create) an employee's allocation for one leave type and cycle.
 * Success: patch the allocation table, refresh history and every balance view.
 * 409 / 404: the numbers moved under us, so refetch the table.
 */
export const useUpdateLeaveAllocation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args) => leaveApi.updateAllocation(args),
    onSuccess: (balance, { employeeId }) => {
      if (balance?.employeeId) patchAllocationPages(qc, balance);
      [
        ["allocationHistory", employeeId],
        queryKeys.leave.balances(employeeId),
        ["employeeBalances", employeeId],
        queryKeys.leave.all,
      ].forEach((queryKey) => qc.invalidateQueries({ queryKey }));
    },
    onError: (error) => {
      const status = error?.response?.status;
      if (status === 409 || status === 404)
        qc.invalidateQueries({ queryKey: ["leaveAllocations"] });
    },
  });
};

/* ---------- Anniversary replenish job ---------- */
export const useReplenishRuns = (limit = 10) =>
  useQuery({
    queryKey: ["replenishRuns", limit],
    queryFn: async () => {
      const res = await leaveApi.getReplenishRuns(limit);
      return Array.isArray(res) ? res : res?.data || [];
    },
  });

/** Run (or dry-run) the job; a real run refreshes the runs list and every balance view. */
export const useRunReplenish = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args) => leaveApi.runReplenish(args),
    onSuccess: (_res, { dryRun }) => {
      qc.invalidateQueries({ queryKey: ["replenishRuns"] });
      if (!dryRun)
        LEAVE_CHANGE_KEYS.forEach((queryKey) =>
          qc.invalidateQueries({ queryKey }),
        );
    },
  });
};

/* ---------- Leave review (approvers / HR) ---------- */

// Query keys as agreed with the backend spec
export const leaveReviewKeys = {
  requests: (filters) => ["leaveRequests", filters],
  count: (status) => ["leaveCount", status],
  request: (id) => ["leaveRequest", id],
  employeeBalances: (employeeId, asOf) => [
    "employeeBalances",
    employeeId,
    asOf,
  ],
};

// After approve / reject / cancel / file: lists, counts, details and balances
// (including the older per-employee screens) all refetch.
const LEAVE_CHANGE_KEYS = [
  ["leaveRequests"],
  ["leaveCount"],
  ["leaveRequest"],
  ["employeeBalances"],
  ["leaveAllocations"],
  queryKeys.leave.all,
  ["leaveList"],
  ["employeeLeaveBalances"],
];

/** Approval queue → { data, total, page, limit } */
export const useLeaveRequests = (filters, { enabled = true } = {}) =>
  useQuery({
    queryKey: leaveReviewKeys.requests(filters),
    queryFn: () => leaveApi.getRequests(filters),
    placeholderData: keepPreviousData,
    enabled,
  });

/** Tab badge: number of requests with this status ("all" = any status). */
export const useLeaveCount = (status, { enabled = true } = {}) =>
  useQuery({
    queryKey: leaveReviewKeys.count(status),
    queryFn: () =>
      leaveApi.getRequests({
        status: status === "all" ? undefined : status,
        page: 1,
        limit: 1,
      }),
    select: (res) => res?.total ?? 0,
    enabled,
  });

/** One request with the employee's balance → { request, balance, canReview } */
export const useLeaveRequest = (id) =>
  useQuery({
    queryKey: leaveReviewKeys.request(id),
    queryFn: () => leaveApi.getRequest(id),
    enabled: !!id,
    retry: false, // 403/404: show the message right away
  });

/** Whether the current user may approve/reject this request (token based). */
export const useCanApproveLeave = (id, { enabled = true } = {}) =>
  useQuery({
    queryKey: ["leaveRequest", id, "canApprove"],
    queryFn: () => leaveApi.canApprove(id),
    enabled: !!id && enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

/** An employee's balances for the cycles containing `asOf` (default today) → LeaveBalance[] */
export const useEmployeeBalances = (employeeId, asOf) =>
  useQuery({
    queryKey: leaveReviewKeys.employeeBalances(employeeId, asOf || "today"),
    queryFn: async () => {
      const res = await leaveApi.getLeaveBalanceEmployee(employeeId, { asOf });
      return Array.isArray(res) ? res : res?.data || [];
    },
    enabled: !!employeeId,
  });

export const useLeaveTypes = () =>
  useQuery({
    queryKey: queryKeys.leave.types(),
    queryFn: async () => {
      const res = await leaveApi.getLeaveTypes();
      return Array.isArray(res) ? res : res?.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });

// 409 = already handled by someone else: the caller still needs fresh data.
const refetchOnConflict = (qc) => (error) => {
  if (error?.response?.status === 409)
    LEAVE_CHANGE_KEYS.forEach((queryKey) => qc.invalidateQueries({ queryKey }));
};

const useLeaveMutation = (fn) => {
  const qc = useQueryClient();
  return useInvalidatingMutation(fn, LEAVE_CHANGE_KEYS, {
    onError: refetchOnConflict(qc),
  });
};

export const useApproveLeave = () =>
  useLeaveMutation(({ id, remarks, allLevels }) =>
    leaveApi.approveLeave(id, remarks, allLevels),
  );
export const useRejectLeave = () =>
  useLeaveMutation(({ id, remarks }) => leaveApi.rejectLeave(id, remarks));
export const useCancelLeave = () =>
  useLeaveMutation(({ id }) => leaveApi.cancelLeave(id));
/** File a leave (for yourself, or for an employee in your scope). */
export const useFileLeave = () =>
  useLeaveMutation((body) => leaveApi.createLeaveRequest(body));
